import type { IconName } from "@/components/ui/icon";

export type TourId =
  | "talent-search"
  | "arbitrator"
  | "insights"
  | "jobs"
  | "pipeline"
  | "outreach";

export type TourPlacement = "top" | "bottom" | "left" | "right";

/**
 * Spotlight tours assume the desktop layout, so they never auto-start or render
 * below this width (the app's `lg` breakpoint). Mobile gets no guided tour.
 */
export const TOUR_MIN_VIEWPORT_WIDTH = 1024;

export interface TourStep {
  key: string;
  /** `data-tour` attribute value to spotlight, or null for a centered step. */
  target: string | null;
  placement: TourPlacement;
  icon: IconName;
  title: string;
  body: string;
  /** Optional preview screenshot (public path) shown on centered/welcome steps. */
  image?: string;
}

export interface TourDefinition {
  id: TourId;
  /** Accessible label for the overlay dialog. */
  label: string;
  steps: TourStep[];
}

export const TOURS: Record<TourId, TourDefinition> = {
  "talent-search": {
    id: "talent-search",
    label: "Talent Search tour",
    steps: [
      {
        key: "welcome",
        target: null,
        placement: "bottom",
        icon: "IconSearchIntelligence",
        title: "Welcome to Talent Search",
        body: "This is where you find candidates. Describe who you're looking for in plain language and Arbi searches millions of profiles for you. Let me show you around, it takes less than a minute.",
        image: "/assets/tours/talent-search-welcome.png",
      },
      {
        key: "tabs",
        target: "ts-tabs",
        placement: "bottom",
        icon: "IconLayoutDashboard",
        title: "One search, two views",
        body: "Every search has two tabs. Search shows your ranked results, scored against your criteria, and Insights maps the whole talent pool. Click a tab any time to switch.",
      },
      {
        key: "filters",
        target: "ts-filters",
        placement: "right",
        icon: "IconTag",
        title: "Refine with filters",
        body: "Narrow your results by title, location, experience, skills and more. Filters combine with your search prompt, so you can start broad and tighten as you go.",
      },
      {
        key: "results",
        target: "ts-results",
        placement: "left",
        icon: "IconPeople",
        title: "Your candidate results",
        body: "Each card is a live profile. Click one to see the full picture: experience, education, contact options and an AI summary of why they match your search.",
      },
      {
        key: "toolbar",
        target: "ts-toolbar",
        placement: "bottom",
        icon: "IconCircleCheck",
        title: "Select and act",
        body: "Select candidates to export them, add them to a job pipeline, or enroll them in an outreach sequence. Switch between cards and the criteria table, or ask the AI assistant for help from here.",
      },
    ],
  },
  arbitrator: {
    id: "arbitrator",
    label: "Arbitrator tour",
    steps: [
      {
        key: "welcome",
        target: null,
        placement: "bottom",
        icon: "IconMagicWand2",
        title: "Meet the Arbitrator",
        body: "The Arbitrator reviews every candidate against your hiring criteria and scores them, so you spend your time on the best fits instead of scrolling. Here's how it works.",
      },
      {
        key: "toolbar",
        target: "arb-toolbar",
        placement: "bottom",
        icon: "IconPencil",
        title: "Tune your criteria",
        body: "Your criteria live here. Edit them any time and the Arbitrator re-scores everyone. You can also track scoring progress and search within scored candidates.",
      },
      {
        key: "table",
        target: "arb-table",
        placement: "top",
        icon: "IconLineChart3",
        title: "Scored candidates",
        body: "Candidates are ranked by how well they match. Click a row to see the reasoning behind each criterion, with evidence pulled from the profile. Select rows to move the best forward in bulk.",
      },
      {
        key: "review",
        target: "arb-review",
        placement: "bottom",
        icon: "IconCircleCheck",
        title: "Review results",
        body: "Step through candidates one by one in the review sheet: accept or reject each criterion's verdict, and the Arbitrator learns your taste as you go.",
      },
    ],
  },
  insights: {
    id: "insights",
    label: "Insights tour",
    steps: [
      {
        key: "welcome",
        target: null,
        placement: "bottom",
        icon: "IconLineChart3",
        title: "Understand your talent pool",
        body: "Insights turns your search results into a market snapshot: where candidates are, what they earn, who employs them and how senior they are. Great for calibrating a search or briefing a hiring manager.",
      },
      {
        key: "panel",
        target: "ins-panel",
        placement: "top",
        icon: "IconLayoutDashboard",
        title: "Your market snapshot",
        body: "Every chart here is built from your current search, so as you refine filters the insights update with you.",
      },
      {
        key: "overview",
        target: "ins-overview",
        placement: "bottom",
        icon: "IconSparklesTwo2",
        title: "Smart overview",
        body: "A written AI summary of the pool: the headline numbers, notable patterns, and what they mean for your search strategy.",
      },
      {
        key: "employers",
        target: "ins-employers",
        placement: "top",
        icon: "IconBuildings",
        title: "Where they work",
        body: "See which companies employ most of your talent pool. Perfect for sourcing target lists or spotting competitors for talent.",
      },
    ],
  },
  jobs: {
    id: "jobs",
    label: "Jobs tour",
    steps: [
      {
        key: "welcome",
        target: null,
        placement: "bottom",
        icon: "IconFileText",
        title: "Welcome to Jobs",
        body: "Jobs is where hiring actually happens. Each job has its own pipeline, arbitrator and outreach sequences, all connected. Here's a quick look.",
      },
      {
        key: "header",
        target: "jobs-header",
        placement: "bottom",
        icon: "IconPlusMedium",
        title: "Create a job",
        body: "Start a new role from here. Paste a job description and Arbi sets up matching criteria for you, or build it from scratch.",
      },
      {
        key: "filters",
        target: "jobs-filters",
        placement: "bottom",
        icon: "IconTag",
        title: "Scope and filter",
        body: "Filter your jobs by status, employer or owner so the list always shows what you're working on right now.",
      },
      {
        key: "table",
        target: "jobs-table",
        placement: "top",
        icon: "IconFolderBookmarks",
        title: "All your roles in one place",
        body: "Every job shows its stage counts and recent activity at a glance. Click a job to open its pipeline.",
      },
      {
        key: "ats",
        target: "jobs-ats",
        placement: "left",
        icon: "IconPuzzle",
        title: "Sync with your ATS",
        body: "Connected an ATS? Import jobs and keep candidate stages in sync automatically from this panel.",
      },
    ],
  },
  pipeline: {
    id: "pipeline",
    label: "Pipeline tour",
    steps: [
      {
        key: "welcome",
        target: null,
        placement: "bottom",
        icon: "IconContacts",
        title: "Your hiring pipeline",
        body: "This is the home for one job: every candidate, their stage, and everything you know about them. Let me show you the key parts.",
      },
      {
        key: "tabs",
        target: "pipe-tabs",
        placement: "bottom",
        icon: "IconLayoutDashboard",
        title: "Everything about this job",
        body: "Switch between the Pipeline, the Arbitrator scoring for this job, Outreach Sequences and Tasks. They all share the same candidates.",
      },
      {
        key: "stages",
        target: "pipe-stages",
        placement: "bottom",
        icon: "IconArrowPathRight",
        title: "Stages at a glance",
        body: "Candidates move through stages from sourced to hired. Click a stage to focus the table on just those candidates.",
      },
      {
        key: "toolbar",
        target: "pipe-toolbar",
        placement: "bottom",
        icon: "IconMagnifyingGlass",
        title: "Find and organize",
        body: "Search within the pipeline, filter, change columns and import candidates from talent search, CSV or your ATS.",
      },
      {
        key: "table",
        target: "pipe-table",
        placement: "top",
        icon: "IconPeople",
        title: "The candidate table",
        body: "Click any candidate to open their full profile drawer with contact details, history and notes. Select multiple rows to move stages or start outreach in bulk.",
      },
    ],
  },
  outreach: {
    id: "outreach",
    label: "Outreach tour",
    steps: [
      {
        key: "welcome",
        target: null,
        placement: "bottom",
        icon: "IconEmail1",
        title: "Automate your outreach",
        body: "Sequences send personalized email and Social touches on a schedule, and stop automatically when someone replies. Here's how to run outreach for this job.",
      },
      {
        key: "header",
        target: "seq-header",
        placement: "bottom",
        icon: "IconPlusMedium",
        title: "Create a sequence",
        body: "Build a new sequence from here. Arbi drafts the steps and copy for you based on the job, then you review and launch.",
      },
      {
        key: "chart",
        target: "seq-chart",
        placement: "bottom",
        icon: "IconLineChart3",
        title: "Track performance",
        body: "Watch sends, opens and replies over time so you know which sequences are working and which need a rewrite.",
      },
      {
        key: "table",
        target: "seq-table",
        placement: "top",
        icon: "IconFileText",
        title: "Your sequences",
        body: "Each row is a sequence with its enrollment counts and reply rates. Click one to edit its steps, manage enrollments and read replies in the inbox.",
      },
      {
        key: "sidebar",
        target: "seq-sidebar",
        placement: "left",
        icon: "IconCircleInfo",
        title: "Overview at a glance",
        body: "A running summary of your outreach for this job: active enrollments, upcoming sends and recent replies.",
      },
    ],
  },
};

export const TOUR_IDS = Object.keys(TOURS) as TourId[];
